import React, { useEffect, useState, useRef } from 'react';
import { View } from 'react-native';
import EventList from '../../../components/facial/EventList';
import { facialService } from '../../../services/central-api/facial';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { FacialParamList } from '../../../typings/Params';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import CamerasDropdown from '../../../components/facial/CamerasDropdown';
import Card from '../../../components/Card';
import Container from '../../../components/Container';
import DataTableContainer from '../../../components/DataTableContainer';
import DataTableHeader from '../../../components/DataTableHeader';
import { translate } from '../../../services/translate';
import Header from '../../../components/Header';
import { WindowInformation } from '../../../services/window-information';
import { useQuery } from '@tanstack/react-query';

interface LiveAlertsParams {
    navigation: Navigation;
}

export default function LiveAlerts({ navigation }: LiveAlertsParams) {
    const { styles } = useStyles(styleSheet);
    const loaders = useRef({ didSelectedCamerasLoad: false });
    const windowInfo = WindowInformation();

    const route = useRoute<RouteProp<FacialParamList, 'LiveAlerts'>>();
    const [filters, setFilters] = useFilters<FacialParamList['LiveAlerts']>({
        ...route.params,
    });

    const [selectedCameraFilter, setSelectedCameraFilter] = useState<string[]>(filters.cameras ?? []);

    const isFocused = useIsFocused();
    const { data, isLoading } = useQuery({
        queryKey: ['facialAlertEvents', filters],
        queryFn: () => facialService.getAlertsIntervalDetections({
            ...filters,
            onlyWithPerson: true,
            limit: 100,
            page: 0
        }),
        enabled: isFocused,
        refetchInterval: (data) => {
            if (!isFocused) {
                return false;
            }
            if (!data?.refreshInterval) {
                return false;
            }

            return data.refreshInterval;
        },
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
    });

    useEffect(() => {
        if (!loaders.current.didSelectedCamerasLoad) {
            loaders.current.didSelectedCamerasLoad = true;
            return;
        }
        setFilters({
            ...filters,
            cameras: selectedCameraFilter,
        });
        navigation.setParams({
            ...filters,
            cameras: selectedCameraFilter,
        });
    }, [selectedCameraFilter]);

    return <Container>
        <Header
            title={translate('liveAlerts')}
        />
        <Card>
            <DataTableHeader
                title={translate('registeredDetections')}
                zIndex={2}
                filter={<View style={styles.header(windowInfo.isMobileOrTablet ? 'column' : 'row')}>
                    <View style={{ flex: 1 }}>
                        <CamerasDropdown setValue={setSelectedCameraFilter} value={selectedCameraFilter} zIndex={2} />
                    </View>
                </View>}
            />
            <DataTableContainer
                dataTable={<EventList
                    events={data?.responseList ?? []}
                    navigation={navigation}
                    isLoading={isLoading}
                    module='facial'
                    page='live-events'
                />}
            />
        </Card>
    </Container>;
}

const styleSheet = createStyleSheet(() => ({
    header: (direction: 'row' | 'column') => ({
        zIndex: 2,
        flexDirection: direction,
        gap: 10,
        minWidth: direction === 'row' ? 900 : undefined,
    })
}));
